import {Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {InfoDto} from "../dto/info.dto";
import {Origin} from "../../libs/enum/origin.enum";
import {inject, Injectable} from "@angular/core";
import {DuckDbHelper} from "./duckdb.helper";
import {InfoRepo} from "../api/info.repo";
import {map} from "rxjs/operators";
import {isDefined} from "../../libs/utils/epic.utils";
import {viewerPathname} from "../../viewer/offline/viewer-offline";

/**
 * Marke und Adressen je Herkunft. Der Viewer hat kein Backend, das sie aus application-info.yml liefern koennte - und er
 * muss auch nach einer Kuendigung noch auf das Impressum der Marke verweisen, bei der die Sicherung entstanden ist.
 * `pathname` braucht nur ACCOUNTING: TEST und STAGING teilen sich Bau und Host, die Stufe steht allein im Pfad.
 */
export function buildViewerInfo(origin: Origin, pathname: string): InfoDto {
  const name = 'Iron Backup';
  switch (origin) {
    case Origin.EINFACHPLUS:
      return new InfoDto({type: origin, name: name, websiteUrl: 'https://einfach.plus', loginUrl: 'https://login.einfach.plus',
                          ironBackupViewerUrl: 'https://iron-backup-viewer.einfach.plus/latest/index.html'});
    case Origin.RUNICCLOUD:
      return new InfoDto({type: origin, name: name, websiteUrl: 'https://runic.cloud', loginUrl: 'https://login.runic.cloud',
                          ironBackupViewerUrl: 'https://iron-backup-viewer.runic.cloud/latest/index.html'});
    case Origin.RUNICGE:
      return new InfoDto({type: origin, name: name, websiteUrl: 'https://runic.ge', loginUrl: 'https://login.runic.ge',
                          ironBackupViewerUrl: 'https://iron-backup-viewer.runic.ge/latest/index.html'});
    default: // Fallback
    case Origin.ACCOUNTING: {
      const loginUrl: string = accountingLoginUrl(pathname);
      return new InfoDto({type: origin, name: name, websiteUrl: loginUrl, loginUrl: loginUrl, ironBackupViewerUrl: accountingViewerUrl(pathname)});
    }
  }
}

/** viewer-publish.mjs legt TEST unter /test/ und STAGING unter /staging/ ab; ohne Stufe im Pfad (lokal) bleibt es STAGING. */
export function accountingLoginUrl(pathname: string): string {
  return isTestStage(pathname) ? 'https://acc-login-test.wip.ge' : 'https://acc-login-staging.wip.ge';
}

/** Wie url.iron-backup-viewer in application-test.yml und application-staging.yml. */
export function accountingViewerUrl(pathname: string): string {
  return `https://acc-iron-backup-viewer.wip.ge/${isTestStage(pathname) ? 'test' : 'staging'}/latest/index.html`;
}

function isTestStage(pathname: string): boolean {
  return pathname.startsWith('/test/');
}

/** Vor der Dateiwahl kennt der Viewer nur seinen Hostnamen: dieselbe Zuordnung wie Origin.getByHeader() im Backend. */
export function originByHostname(hostname: string): Origin {
  const host: string = hostname.toLowerCase();
  if (host.includes('einfach.plus')) {
    return Origin.EINFACHPLUS;
  } else if (host.includes('runic.ge')) {
    return Origin.RUNICGE;
  } else if (host.includes('wip.ge') || host.includes('localhost')) {
    return Origin.ACCOUNTING;
  }
  return Origin.RUNICCLOUD;
}

@Injectable({providedIn: 'root'})
export class InfoDuckDb extends InfoRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);

  private info: InfoDto | undefined;

  public override getInfo(): Observable<WrapperDto<InfoDto>> {
    if (isDefined(this.info)) {
      return DuckDbHelper.dataObs('InfoDto', this.info);
    }

    // Ueber den Helfer, nicht per eigenem http.post: Nur dort wird das Sitzungstoken gesetzt, ohne das
    // der Viewer die Abfrage mit 403 abweist - und der Fusszeile fehlten dauerhaft Marke, Link und Version.
    return this.duck.sql('SELECT origin FROM org').pipe(map((res: any) => {
      const origin: Origin = res[0].origin;
      this.info = buildViewerInfo(origin, viewerPathname());
      return DuckDbHelper.data('InfoDto', this.info);
    }));
  }
}
